import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MousePointerClick } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const CTRCalculator = () => {
  const [clicks, setClicks] = useState<string>("");
  const [impressions, setImpressions] = useState<string>("");
  const [result, setResult] = useState<number | null>(null);

  const calculateCTR = () => {
    const clicksNum = parseFloat(clicks) || 0;
    const impressionsNum = parseFloat(impressions) || 0;
    
    if (impressionsNum === 0) {
      setResult(null);
      return;
    }

    const ctr = (clicksNum / impressionsNum) * 100;
    setResult(ctr);
  };

  const reset = () => {
    setClicks("");
    setImpressions("");
    setResult(null);
  };

  const getCTRAssessment = (ctr: number) => {
    if (ctr >= 5) return { label: "Excellent", color: "text-green-600" };
    if (ctr >= 3) return { label: "Good", color: "text-green-500" };
    if (ctr >= 2) return { label: "Average", color: "text-yellow-600" };
    if (ctr >= 1) return { label: "Below Average", color: "text-orange-500" };
    return { label: "Needs Improvement", color: "text-red-600" };
  };

  const relatedTools = getRelatedTools("ctr-calculator");

  return (
    <ToolPageLayout
      title="CTR Calculator"
      description="Calculate your Click-Through Rate to measure how effectively your ads and content drive user engagement."
      metaDescription="CTR (Click-Through Rate) measures the percentage of people who click on your ad or link after seeing it. It's a fundamental metric for evaluating ad relevance, email performance, and content engagement."
      icon={MousePointerClick}
      slug="ctr-calculator"
      formula={{
        equation: "CTR = (Clicks / Impressions) × 100",
        variables: [
          { name: "Clicks", description: "Total number of clicks on your ad, link, or content" },
          { name: "Impressions", description: "Total number of times your ad or content was shown" },
        ]
      }}
      example={{
        inputs: [
          { label: "Clicks", value: "150" },
          { label: "Impressions", value: "5,000" },
        ],
        output: "3.00% CTR",
        interpretation: "3% of people who saw your ad clicked on it. This is a solid CTR for most display and search campaigns."
      }}
      improvements={[
        "Write compelling headlines that speak to user intent",
        "Use strong, action-oriented calls-to-action",
        "Improve ad relevance with tighter keyword targeting",
        "A/B test multiple ad variations to find winners",
        "Optimize for mobile users with appropriate ad formats",
        "Use ad extensions to provide more clickable options",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "Paid Marketing", href: "/paid-marketing" }}
    >
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Calculate CTR</CardTitle>
          <CardDescription>Enter your clicks and impressions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="clicks">Total Clicks</Label>
            <Input
              id="clicks"
              type="number"
              placeholder="150"
              value={clicks}
              onChange={(e) => setClicks(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="impressions">Total Impressions</Label>
            <Input
              id="impressions"
              type="number"
              placeholder="5000"
              value={impressions}
              onChange={(e) => setImpressions(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={calculateCTR} className="flex-1">
              Calculate CTR
            </Button>
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>

          {result !== null && (
            <div className="mt-6 p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Your CTR</p>
                <p className={`text-4xl font-bold ${getCTRAssessment(result).color}`}>
                  {result.toFixed(2)}%
                </p>
                <p className={`text-sm mt-3 ${getCTRAssessment(result).color}`}>
                  {getCTRAssessment(result).label}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default CTRCalculator;
